import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { requestReactivation, confirmReactivation } from "../../services/api/userService";
import "../../styles/features/auth.css";

const MIN_PASSWORD_LENGTH = 8;

/**
 * Account reactivation (PR-B08), the same way as forgot-password:
 *  - without ?token: ask for the email; the server emails a one-time link
 *    (always the same answer, so it never reveals which accounts exist);
 *  - with ?token (the emailed link): choose a new password, which
 *    reactivates the account.
 */
function ReactivateAccount() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleRequest = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      await requestReactivation({ email });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Failed to submit request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setIsLoading(true);
    try {
      await confirmReactivation(token, password);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "This reactivation link is invalid or has expired.");
    } finally {
      setIsLoading(false);
    }
  };

  if (done) {
    return (
      <div className="auth-container">
        <div className="auth-form" style={{ textAlign: "center" }}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>{token ? "✅" : "📧"}</div>
          <h2>{token ? "Account Reactivated!" : "Check your email"}</h2>
          {token ? (
            <p style={{ color: "#555", marginBottom: "1.5rem" }}>
              Your account is active again. Log in with your new password.
            </p>
          ) : (
            <p style={{ color: "#555", marginBottom: "1.5rem" }}>
              If <strong>{email}</strong> belongs to a deactivated account that can be reactivated,
              we've sent it a reactivation link. The link expires in 1 hour.
            </p>
          )}
          <Link
            to="/login"
            className="auth-button"
            style={{ display: "inline-block", textDecoration: "none" }}
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h2>Reactivate Your Account</h2>
        {token ? (
          <form onSubmit={handleConfirm}>
            <p style={{ color: "#555", marginBottom: "1.25rem", fontSize: "0.95rem" }}>
              Choose a new password to reactivate your account.
            </p>
            <div className="form-group">
              <label htmlFor="ra-password">New password</label>
              <input
                id="ra-password"
                name="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="ra-confirm">Confirm new password</label>
              <input
                id="ra-confirm"
                name="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => { setConfirm(e.target.value); setError(""); }}
                required
              />
            </div>
            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? "Reactivating…" : "Reactivate My Account"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRequest}>
            <p style={{ color: "#555", marginBottom: "1.25rem", fontSize: "0.95rem" }}>
              If you deactivated your Sportifide account, enter its email and we'll send you a
              link to reactivate it.
            </p>
            <div className="form-group">
              <label htmlFor="ra-email">Email Address <span style={{ color: "red" }}>*</span></label>
              <input
                id="ra-email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                required
              />
            </div>
            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? "Sending…" : "Email Me a Reactivation Link"}
            </button>
          </form>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="auth-links">
          <Link to="/login">Remember your password? Login</Link>
          <br />
          <span style={{ color: "#555", fontSize: "0.85rem" }}>
            Need help?{" "}
            <a href="mailto:hello@sportifide.com" style={{ color: "inherit" }}>
              hello@sportifide.com
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}

export default ReactivateAccount;
